import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { STATUS } from "@/constants";
import {
  type DomainQueryPreview,
  isDomainQueryPreview,
  isScheduledJob,
  isScheduledJobRun,
  type ScheduledJob,
  type ScheduledJobRun,
} from "@/features/data-hub/model/scheduledJob";
import { apiFetch } from "@/shared/api/apiFetch";
import { readApiErrorMessage } from "@/shared/api/parseApiErrorMessage";

type RequestStatus = "idle" | "loading" | "succeeded" | "failed";

/**
 * Scheduled jobs are a **tenant-wide** resource: `GET /v1/scheduled-jobs` returns every definition
 * of every type for every domain, because `job_type` and the owning domain both live in the row
 * rather than in the path. So this slice stores the unfiltered list and `useScheduledJobs` narrows
 * it to the jobs belonging to one domain. Filtering here instead would make the cache mean
 * "whichever domain asked last", which breaks as soon as two tabs share the store.
 */
export type ScheduledJobsState = {
  jobs: ScheduledJob[];
  status: RequestStatus;
  error: string | null;
  saveStatus: RequestStatus;
  saveError: string | null;
  /** Run history per definition id. Fetched on demand, so most ids are absent. */
  runsByJobId: Record<number, ScheduledJobRun[]>;
  runningJobId: number | null;
  runError: string | null;
  deletingJobId: number | null;
  deleteError: string | null;
};

export const scheduledJobsInitialState: ScheduledJobsState = {
  jobs: [],
  status: STATUS.IDLE,
  error: null,
  saveStatus: STATUS.IDLE,
  saveError: null,
  runsByJobId: {},
  runningJobId: null,
  runError: null,
  deletingJobId: null,
  deleteError: null,
};

const JOBS_PATH = "/v1/scheduled-jobs";

function sortByName(jobs: ScheduledJob[]): ScheduledJob[] {
  return [...jobs].sort((first, second) =>
    first.name.localeCompare(second.name),
  );
}

export const fetchScheduledJobs = createAsyncThunk<
  ScheduledJob[],
  void,
  { rejectValue: string }
>("scheduledJobs/fetch", async (_arg, { rejectWithValue }) => {
  try {
    const res = await apiFetch(JOBS_PATH);
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!Array.isArray(data) || !data.every(isScheduledJob)) {
      return rejectWithValue("Unexpected response shape");
    }
    return data;
  } catch (e) {
    return rejectWithValue(e instanceof Error ? e.message : "Request failed");
  }
});

/**
 * One create request, with the payload already built.
 *
 * The caller builds `payload` rather than passing a draft, because its shape is whatever
 * `jobType` says it is — see `buildJobPayload` in `CreateJobModal`. That keeps this thunk, and
 * therefore this slice, free of any per-type knowledge.
 */
export type CreateScheduledJobArgs = {
  jobType: string;
  name: string;
  description: string;
  /** A five-field cron expression, UTC. Omitted lets the backend apply its own default. */
  schedule?: string;
  /** Whether cron may fire it. Omitted creates the job disabled, which is the backend's default. */
  isEnabled?: boolean;
  payload: Record<string, unknown>;
};

export const createScheduledJob = createAsyncThunk<
  ScheduledJob,
  CreateScheduledJobArgs,
  { rejectValue: string }
>(
  "scheduledJobs/create",
  async (
    { jobType, name, description, schedule, isEnabled, payload },
    { rejectWithValue },
  ) => {
    try {
      const res = await apiFetch(JOBS_PATH, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          // Sent as null rather than "" so the column is cleared, not blanked.
          description: description.trim() === "" ? null : description.trim(),
          job_type: jobType,
          // Spread rather than always sent: an absent key lets the backend apply its own default,
          // which is what a caller with no schedule picker in front of it wants.
          ...(schedule === undefined ? {} : { schedule }),
          ...(isEnabled === undefined ? {} : { is_enabled: isEnabled }),
          payload,
        }),
      });
      if (!res.ok) {
        return rejectWithValue(await readApiErrorMessage(res));
      }
      const data: unknown = await res.json();
      if (!isScheduledJob(data)) {
        return rejectWithValue("Unexpected response shape");
      }
      return data;
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : "Request failed");
    }
  },
);

/**
 * Trigger one run now. Works regardless of `is_enabled` — that flag governs cron only.
 *
 * Returns 202 with the new `background_jobs` id; there is no progress stream for this job type, so
 * the caller refetches the history to find out how it went.
 */
export type UpdateScheduledJobArgs = {
  jobId: number;
  /**
   * Row-level fields, sent only when they are being changed. Omitted keys are left as stored —
   * the request model defaults every field to `None` and applies only what is supplied, so a
   * caller editing the payload alone never has to echo the name back.
   */
  name?: string;
  description?: string;
  /**
   * **Only the keys being changed.** The backend merges them over the stored payload and then
   * re-validates the whole thing, so sending a partial object leaves everything else exactly as
   * saved — which matters most for `query`, a statement that can run to thousands of characters
   * and that no caller should risk round-tripping a stale copy of.
   *
   * A key set to `null` clears it. Immutable keys such as `domain_id` are restored from the stored
   * row whatever is sent, so an update cannot repoint a definition at another domain.
   */
  payload: Record<string, unknown>;
  /** A five-field cron expression, UTC. Absent leaves the stored cadence alone. */
  schedule?: string;
  /** Absent leaves the switch as it is, which is what a payload-only edit wants. */
  isEnabled?: boolean;
};

/** Change part of a definition's payload in place, keeping the job and its history. */
export const updateScheduledJob = createAsyncThunk<
  ScheduledJob,
  UpdateScheduledJobArgs,
  { rejectValue: string }
>(
  "scheduledJobs/update",
  async (
    { jobId, name, description, payload, schedule, isEnabled },
    { rejectWithValue },
  ) => {
    try {
      const res = await apiFetch(`${JOBS_PATH}/${jobId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        // Built by spreading rather than always sending the keys: `name: undefined` survives
        // `JSON.stringify` as an absent key, but `description: null` does not — and null is how the
        // backend clears a description, so the two must stay distinguishable.
        body: JSON.stringify({
          ...(name === undefined ? {} : { name }),
          ...(description === undefined ? {} : { description }),
          ...(schedule === undefined ? {} : { schedule }),
          ...(isEnabled === undefined ? {} : { is_enabled: isEnabled }),
          payload,
        }),
      });
      if (!res.ok) {
        return rejectWithValue(await readApiErrorMessage(res));
      }
      const data: unknown = await res.json();
      if (!isScheduledJob(data)) {
        return rejectWithValue("Unexpected response shape");
      }
      return data;
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : "Request failed");
    }
  },
);

export const runScheduledJobNow = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>("scheduledJobs/run", async (jobId, { rejectWithValue }) => {
  try {
    const res = await apiFetch(`${JOBS_PATH}/${jobId}/runs`, {
      method: "POST",
    });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    return jobId;
  } catch (e) {
    return rejectWithValue(e instanceof Error ? e.message : "Request failed");
  }
});

export const fetchScheduledJobRuns = createAsyncThunk<
  { jobId: number; runs: ScheduledJobRun[] },
  number,
  { rejectValue: string }
>("scheduledJobs/fetchRuns", async (jobId, { rejectWithValue }) => {
  try {
    const res = await apiFetch(`${JOBS_PATH}/${jobId}/runs`);
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    const data: unknown = await res.json();
    if (!Array.isArray(data) || !data.every(isScheduledJobRun)) {
      return rejectWithValue("Unexpected response shape");
    }
    return { jobId, runs: data };
  } catch (e) {
    return rejectWithValue(e instanceof Error ? e.message : "Request failed");
  }
});

export const deleteScheduledJob = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>("scheduledJobs/delete", async (jobId, { rejectWithValue }) => {
  try {
    const res = await apiFetch(`${JOBS_PATH}/${jobId}`, { method: "DELETE" });
    if (!res.ok) {
      return rejectWithValue(await readApiErrorMessage(res));
    }
    return jobId;
  } catch (e) {
    return rejectWithValue(e instanceof Error ? e.message : "Request failed");
  }
});

export type PreviewDomainQueryArgs = {
  domainId: number;
  sql: string;
  limit?: number;
};

/**
 * Run a query against the domain's connection and return a sample.
 *
 * Not stored in this slice: the preview belongs to one open form, and keeping it in the store would
 * outlive the modal and reappear the next time it opens. The caller holds the result in component
 * state. A query that fails to run resolves with a populated `error` rather than rejecting —
 * invalid SQL is the expected state of a statement someone is still typing.
 */
export const previewDomainQuery = createAsyncThunk<
  DomainQueryPreview,
  PreviewDomainQueryArgs,
  { rejectValue: string }
>(
  "scheduledJobs/previewQuery",
  async ({ domainId, sql, limit }, { rejectWithValue }) => {
    try {
      const res = await apiFetch(`/v1/domains/${domainId}/queries/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(limit === undefined ? { sql } : { sql, limit }),
      });
      if (!res.ok) {
        return rejectWithValue(await readApiErrorMessage(res));
      }
      const data: unknown = await res.json();
      if (!isDomainQueryPreview(data)) {
        return rejectWithValue("Unexpected response shape");
      }
      return data;
    } catch (e) {
      return rejectWithValue(e instanceof Error ? e.message : "Request failed");
    }
  },
);

const scheduledJobsSlice = createSlice({
  name: "scheduledJobs",
  initialState: scheduledJobsInitialState,
  reducers: {
    clearScheduledJobSaveError(state) {
      state.saveStatus = STATUS.IDLE;
      state.saveError = null;
    },
    clearScheduledJobActionErrors(state) {
      state.runError = null;
      state.deleteError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchScheduledJobs.pending, (state) => {
        state.status = STATUS.LOADING;
        state.error = null;
      })
      .addCase(fetchScheduledJobs.fulfilled, (state, action) => {
        state.status = STATUS.SUCCEEDED;
        state.jobs = sortByName(action.payload);
        state.error = null;
      })
      .addCase(fetchScheduledJobs.rejected, (state, action) => {
        state.status = STATUS.FAILED;
        state.error = action.payload ?? "Request failed";
      })
      .addCase(createScheduledJob.pending, (state) => {
        state.saveStatus = STATUS.LOADING;
        state.saveError = null;
      })
      .addCase(createScheduledJob.fulfilled, (state, action) => {
        state.saveStatus = STATUS.SUCCEEDED;
        state.jobs = sortByName([...state.jobs, action.payload]);
        state.saveError = null;
      })
      .addCase(createScheduledJob.rejected, (state, action) => {
        state.saveStatus = STATUS.FAILED;
        state.saveError = action.payload ?? "Request failed";
      })
      .addCase(updateScheduledJob.pending, (state) => {
        state.saveStatus = STATUS.LOADING;
        state.saveError = null;
      })
      .addCase(updateScheduledJob.fulfilled, (state, action) => {
        state.saveStatus = STATUS.SUCCEEDED;
        // Replaced rather than merged: the response is the row as stored, so anything the backend
        // normalised or restored on the way in is what the list should now show.
        state.jobs = sortByName(
          state.jobs.map((job) =>
            job.id === action.payload.id ? action.payload : job,
          ),
        );
        state.saveError = null;
      })
      .addCase(updateScheduledJob.rejected, (state, action) => {
        state.saveStatus = STATUS.FAILED;
        state.saveError = action.payload ?? "Request failed";
      })
      .addCase(runScheduledJobNow.pending, (state, action) => {
        state.runningJobId = action.meta.arg;
        state.runError = null;
      })
      .addCase(runScheduledJobNow.fulfilled, (state) => {
        state.runningJobId = null;
        state.runError = null;
      })
      .addCase(runScheduledJobNow.rejected, (state, action) => {
        state.runningJobId = null;
        state.runError = action.payload ?? "Request failed";
      })
      .addCase(fetchScheduledJobRuns.fulfilled, (state, action) => {
        state.runsByJobId[action.payload.jobId] = action.payload.runs;
      })
      .addCase(deleteScheduledJob.pending, (state, action) => {
        state.deletingJobId = action.meta.arg;
        state.deleteError = null;
      })
      .addCase(deleteScheduledJob.fulfilled, (state, action) => {
        state.deletingJobId = null;
        state.jobs = state.jobs.filter((job) => job.id !== action.payload);
        delete state.runsByJobId[action.payload];
        state.deleteError = null;
      })
      .addCase(deleteScheduledJob.rejected, (state, action) => {
        state.deletingJobId = null;
        state.deleteError = action.payload ?? "Request failed";
      });
  },
});

export const { clearScheduledJobSaveError, clearScheduledJobActionErrors } =
  scheduledJobsSlice.actions;
export const scheduledJobsReducer = scheduledJobsSlice.reducer;
